/**
 * Formats duration in seconds to a human-readable HH:MM:SS format
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (HH:MM:SS or MM:SS or SS)
 */
export function formatDuration(seconds: number): string {
  if (seconds < 0) {
    return '00:00';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  // If duration is less than 1 minute, show only seconds
  if (hours === 0 && minutes === 0) {
    return `00:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  // If duration is less than 1 hour, show MM:SS
  if (hours === 0) {
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  // Otherwise show HH:MM:SS
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
}

/**
 * Formats duration in seconds to a verbose human-readable format
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (e.g., "2h 30m 15s")
 */
export function formatDurationVerbose(seconds: number): string {
  if (seconds < 0) {
    return '0s';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }

  if (remainingSeconds > 0 || parts.length === 0) {
    parts.push(`${remainingSeconds}s`);
  }

  return parts.join(' ');
}